import {connect} from 'react-redux';

import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {CancelToken} from '../../../../../libs/shared/helpers/src/utilities/asyncCancelHelpers';
import {DropdownProps} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {
  AppParametricSelector,
  createSelector,
  makeMapStateToProps,
} from '../../../../../srcCommon/helpers/reselectHelpers';
import {fuzzySearchBuilder} from '../../../../helpers/fuzzySearchHelpers';
import {AnswerFolderModel} from '../../../../models/api/answerFolderModel';
import {AppState} from '../../../../store/appState';
import {makeGetAnswerFoldersInNamespaces} from '../../../../store/data/answerFolders/answerFoldersSelectors';
import {AnswerFolderDropdown} from './answerFolderDropdown';
import {hierarchicalFolderSort} from './hierarchicalFolderSort';

/*
 * Props + Types.
 */

interface AnswerFolderDropdownContainerProps extends DropdownProps {
  onFolderSelect: HandlerOf<number | undefined>;
  selectedFolder: AnswerFolderModel | undefined;
  currentFolder: AnswerFolderModel | undefined;
  namespaces: ReadonlyArray<string>;
}

type GetFoldersSelector = AppParametricSelector<
  AnswerFolderDropdownContainerProps,
  ReadonlyArray<AnswerFolderModel>
>;

/*
 * Selectors.
 */

function getNamespaces(_state: AppState, props: AnswerFolderDropdownContainerProps) {
  return props.namespaces;
}
function getCurrentFolder(_state: AppState, props: AnswerFolderDropdownContainerProps) {
  return props.currentFolder;
}

function makeGetFoldersInHierarchyOrder(getAnswerFoldersInNamespace: GetFoldersSelector) {
  return createSelector([getAnswerFoldersInNamespace], (answerFoldersInNamespace) =>
    hierarchicalFolderSort(answerFoldersInNamespace),
  );
}

const answerFolderFuzzySearcher = fuzzySearchBuilder<AnswerFolderModel>(
  (folder: AnswerFolderModel) => folder.name,
);

// NOTE: This is actually being fetched syncronously at the moment, but will move to being dynamically fetched shortly after
//       Canned Response Folders launches. It's been built inside the bones of an async operation in preparation for this.
function makeFetchAnswerFoldersAsync(getFoldersInHierarchyOrder: GetFoldersSelector) {
  return createSelector(
    [getFoldersInHierarchyOrder, getCurrentFolder],
    (answerFolders, currentFolder) =>
      async function fetchAsync(
        query: string,
        _answerFolders: ReadonlyArray<AnswerFolderModel | null>,
        _cancelToken: CancelToken,
      ) {
        const folders = !query ? answerFolders : answerFolderFuzzySearcher(answerFolders, query);

        return Promise.resolve({
          items: [null, ...folders.filter((f) => (currentFolder ? f.id !== currentFolder.id : true))],
          canLoadMore: false,
        });
      },
  );
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<AnswerFolderDropdownContainerProps>()(() => {
  const getFoldersInNamespaces = makeGetAnswerFoldersInNamespaces(getNamespaces);
  const getFoldersInHierarchyOrder = makeGetFoldersInHierarchyOrder(getFoldersInNamespaces);

  return {
    fetchAnswerFoldersAsync: makeFetchAnswerFoldersAsync(getFoldersInHierarchyOrder),
  };
});

export const AnswerFolderDropdownContainer = connect(mapStateToProps)(AnswerFolderDropdown);
